import { useMemo } from 'react';
import { icpRanksOf, type IcpItem, type IcpRankContext, type IcpRanked, type IcpView, type JobDetail, type RankedBy, type RankingRow } from '@nest-recruit/shared';
import { useIsFieldHidden } from '../../../../stores/session';
import { useIcp } from '../../../icp/api/icp-api';
import { ratingsKeyOf, withRatings, type RatingOverlay } from '../../../icp/icp-form';
import { withProfileRanks } from '../../icp-view';

// ADR 0062 §2.7: the profile's ratings are on screen the moment they are clicked, and everyone's rank is worked out here from them
// with the same function the server ranks with. Ranking, the Review board and anything else that lists people in the job's order read
// this one hook, so they always agree — the stored ranking catches up about 3 seconds after a click.

const NO_ITEMS: readonly IcpItem[] = [];
const NO_OVERLAY: RatingOverlay = {};

interface ProfileRanksInput {
  readonly jobId: string;
  /** What the ranking's rows say about the job: remote or not, and whether there is a budget to judge a salary against. */
  readonly job: Pick<JobDetail, 'workMode' | 'salaryBudget'>;
  readonly rows: readonly RankingRow[];
  /** `RankingView.rankedBy`: a job still in its old order is ranked by the criteria, and its rows are left as stored. */
  readonly rankedBy: RankedBy;
  /** False reads nothing (the profile is not asked for). */
  readonly isEnabled?: boolean;
  /** Ratings clicked on this screen and not yet confirmed, laid over the saved ones (power sorting). The Review board has none. */
  readonly overlay?: RatingOverlay;
}

export interface ProfileRanks {
  readonly view: IcpView | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly refetch: () => void;
  /** The lines with the ratings on screen (the server's, with unconfirmed clicks laid over). */
  readonly items: readonly IcpItem[];
  readonly ratingsKey: string;
  readonly hasProfile: boolean;
  readonly isJevConnected: boolean;
  /** Everyone's rank and per-line values, by application id; undefined without a profile or while the job is in its old order. */
  readonly ranks: ReadonlyMap<string, IcpRanked> | undefined;
  /** The rows with the profile's match, stars, tab and key laid over them (or as stored when there is nothing to lay). */
  readonly rankedRows: readonly RankingRow[];
}

export function useProfileRanks({ jobId, job, rows, rankedBy, isEnabled = true, overlay = NO_OVERLAY }: ProfileRanksInput): ProfileRanks {
  const query = useIcp(jobId, isEnabled);
  const view = query.data;
  const profile = view?.profile ?? null;
  const items = useMemo(() => withRatings(profile?.items ?? NO_ITEMS, overlay), [profile, overlay]);
  const ratingsKey = useMemo(() => ratingsKeyOf(items), [items]);
  const isJevConnected = view?.isJevConnected ?? true;
  const canSeeSalary = !useIsFieldHidden('candidate.expectedSalary');
  const hasBudget = job.salaryBudget.maxMinor !== null || rows.some((row) => row.application.expectedSalaryWithinBudget !== null);
  const ctx = useMemo<IcpRankContext>(() => ({ job: { isRemote: job.workMode === 'remote', hasBudget }, canSeeSalary, isJevConnected }), [job.workMode, hasBudget, canSeeSalary, isJevConnected]);
  const ranks = useMemo(() => (profile && rankedBy === 'profile' ? icpRanksOf(items, rows, ctx) : undefined), [profile, rankedBy, items, rows, ctx]);
  const rankedRows = useMemo(() => (ranks ? withProfileRanks(rows, ranks) : rows), [ranks, rows]);
  return { view, isLoading: query.isPending && isEnabled, isError: query.isError && !view, refetch: () => void query.refetch(), items, ratingsKey, hasProfile: profile !== null, isJevConnected, ranks, rankedRows };
}
